import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import { soccerPath, runningPath, hikingPath } from '../utils/paths';
import { mediaUrl } from '../utils/blogUtils';
import { compressImageForUpload, isImageFile } from '../utils/imageUpload';
import { CLUB_SPORTS, normalizeClubSport } from '../config/clubSports';
import './ClubRegistration.css';

const emptyPreview = { logo: '', landing: '' };

const ClubRegistration = ({ defaultSport = 'SOCCER', successBasePath = null } = {}) => {
  const navigate = useNavigate();
  const { user, isAdmin, isClubCreator, isAuthenticated } = useAuth();
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    sport: normalizeClubSport(defaultSport),
    logoUrl: '',
    imageUrl: '',
  });
  const [logoFile, setLogoFile] = useState(null);
  const [landingFile, setLandingFile] = useState(null);
  const [previews, setPreviews] = useState(emptyPreview);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [hasApprovedPlayer, setHasApprovedPlayer] = useState(false);
  const [checkingPlayer, setCheckingPlayer] = useState(true);
  const logoInputRef = useRef(null);
  const landingInputRef = useRef(null);

  useEffect(() => {
    const checkApprovedPlayer = async () => {
      if (!isAuthenticated || !user) {
        setCheckingPlayer(false);
        return;
      }

      if (isAdmin() || isClubCreator()) {
        setHasApprovedPlayer(true);
        setCheckingPlayer(false);
        return;
      }

      try {
        const allPlayers = await api.getAllPlayers(user?.id);
        const userPlayer = allPlayers.find((p) => p.userId === user.id);
        setHasApprovedPlayer(!!userPlayer);
      } catch {
        setHasApprovedPlayer(false);
      } finally {
        setCheckingPlayer(false);
      }
    };

    checkApprovedPlayer();
  }, [isAuthenticated, user, isAdmin, isClubCreator]);

  useEffect(() => {
    return () => {
      if (previews.logo?.startsWith('blob:')) URL.revokeObjectURL(previews.logo);
      if (previews.landing?.startsWith('blob:')) URL.revokeObjectURL(previews.landing);
    };
  }, [previews.logo, previews.landing]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    setError('');
  };

  const handleFileSelect = (type, file) => {
    if (!file) return;
    if (!isImageFile(file)) {
      setError('Please choose a JPG, PNG, GIF, or WebP image.');
      return;
    }

    const objectUrl = URL.createObjectURL(file);
    if (type === 'logo') {
      if (previews.logo?.startsWith('blob:')) URL.revokeObjectURL(previews.logo);
      setLogoFile(file);
      setPreviews((prev) => ({ ...prev, logo: objectUrl }));
      setFormData((prev) => ({ ...prev, logoUrl: '' }));
      if (logoInputRef.current) logoInputRef.current.value = '';
    } else {
      if (previews.landing?.startsWith('blob:')) URL.revokeObjectURL(previews.landing);
      setLandingFile(file);
      setPreviews((prev) => ({ ...prev, landing: objectUrl }));
      setFormData((prev) => ({ ...prev, imageUrl: '' }));
      if (landingInputRef.current) landingInputRef.current.value = '';
    }
    setError('');
  };

  const clearFile = (type) => {
    if (type === 'logo') {
      if (previews.logo?.startsWith('blob:')) URL.revokeObjectURL(previews.logo);
      setLogoFile(null);
      setPreviews((prev) => ({ ...prev, logo: '' }));
    } else {
      if (previews.landing?.startsWith('blob:')) URL.revokeObjectURL(previews.landing);
      setLandingFile(null);
      setPreviews((prev) => ({ ...prev, landing: '' }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const sport = normalizeClubSport(formData.sport);
      const created = await api.registerClub(
        {
          name: formData.name.trim(),
          description: formData.description.trim() || null,
          sport,
          logoUrl: logoFile ? null : formData.logoUrl.trim() || null,
          imageUrl: landingFile ? null : formData.imageUrl.trim() || null,
        },
        user.id
      );

      let uploadWarning = '';
      try {
        if (logoFile && created?.id) {
          const compressed = await compressImageForUpload(logoFile);
          await api.uploadClubImage(created.id, 'logo', compressed, user.id);
        }
        if (landingFile && created?.id) {
          const compressed = await compressImageForUpload(landingFile);
          await api.uploadClubImage(created.id, 'landing', compressed, user.id);
        }
      } catch (uploadErr) {
        uploadWarning =
          uploadErr.message ||
          'Club was created, but image upload failed. You can add images later from club settings.';
      }

      setSuccess(
        uploadWarning
          ? `Club registration submitted. ${uploadWarning}`
          : 'Club registration submitted successfully! Waiting for admin approval.'
      );
      setTimeout(() => {
        if (successBasePath) {
          navigate(`${successBasePath}/clubs/list`);
        } else if (sport === 'RUNNING') {
          navigate(runningPath('/clubs/list'));
        } else if (sport === 'HIKING') {
          navigate(hikingPath('/clubs/list'));
        } else {
          navigate(`${soccerPath('/clubs/list')}?sport=${encodeURIComponent(sport)}`);
        }
      }, 2200);
    } catch (err) {
      setError(err.message || 'Failed to register club');
    } finally {
      setLoading(false);
    }
  };

  if (checkingPlayer) {
    return (
      <div className="card club-registration">
        <div className="loading">Checking access...</div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="card club-registration">
        <h2>Please log in to register a club</h2>
      </div>
    );
  }

  const canRegisterClub = isAdmin() || isClubCreator() || hasApprovedPlayer;

  if (!canRegisterClub) {
    return (
      <div className="card club-registration">
        <h2>Access Restricted</h2>
        <div className="alert alert-warning">
          <p>You need to be an approved player to register a club.</p>
          <p>Please wait for system admin approval after registering as a player.</p>
          <button
            onClick={() =>
              navigate(successBasePath ? `${successBasePath}/clubs/list` : soccerPath('/clubs/list'))
            }
            className="btn btn-primary"
            style={{ marginTop: '1rem' }}
          >
            View Clubs
          </button>
        </div>
      </div>
    );
  }

  const logoPreviewSrc = previews.logo || (formData.logoUrl ? mediaUrl(formData.logoUrl) : '');
  const landingPreviewSrc =
    previews.landing || (formData.imageUrl ? mediaUrl(formData.imageUrl) : '');
  const selectedSport = CLUB_SPORTS.find((sport) => sport.id === formData.sport);

  return (
    <div className="card club-registration">
      <div className="club-registration__header">
        <button
          type="button"
          onClick={() =>
            navigate(successBasePath ? successBasePath : soccerPath('/'))
          }
          className="btn btn-secondary"
        >
          ← Back
        </button>
        <h2>Register a New Club</h2>
      </div>
      <p className="club-registration__intro">
        Pick one sport per club. Soccer, Running, and Hiking stay separate — and any sport you add
        later gets its own club list too.
      </p>

      {error && <div className="alert alert-error">{error}</div>}
      {success && <div className="alert alert-success">{success}</div>}

      <form onSubmit={handleSubmit}>
        <fieldset className="club-registration__sports">
          <legend>Sport *</legend>
          <div className="club-registration__sport-options" role="radiogroup" aria-label="Club sport">
            {CLUB_SPORTS.map((sport) => (
              <label
                key={sport.id}
                className={`club-registration__sport-option${
                  formData.sport === sport.id ? ' is-selected' : ''
                }`}
              >
                <input
                  type="radio"
                  name="sport"
                  value={sport.id}
                  checked={formData.sport === sport.id}
                  onChange={handleChange}
                  required
                />
                <span className="club-registration__sport-label">{sport.label}</span>
                <span className="club-registration__sport-desc">{sport.description}</span>
              </label>
            ))}
          </div>
          {selectedSport && (
            <p className="form-help">
              This will be a <strong>{selectedSport.label}</strong> club only — it will not mix with
              other sports in the club directory.
            </p>
          )}
        </fieldset>

        <div className="form-group">
          <label htmlFor="club-name">Club Name *</label>
          <input
            id="club-name"
            type="text"
            name="name"
            value={formData.name}
            onChange={handleChange}
            required
            minLength={2}
            maxLength={100}
            placeholder="Enter club name"
          />
        </div>

        <div className="form-group">
          <label htmlFor="club-description">Description</label>
          <textarea
            id="club-description"
            name="description"
            value={formData.description}
            onChange={handleChange}
            rows="4"
            maxLength={500}
            placeholder="Tell members what this club is about"
          />
        </div>

        <div className="club-registration__media-grid">
          <div className="form-group club-registration__media">
            <label htmlFor="club-logo-file">Club logo</label>
            <input
              id="club-logo-file"
              ref={logoInputRef}
              type="file"
              accept="image/jpeg,image/png,image/gif,image/webp"
              onChange={(e) => handleFileSelect('logo', e.target.files?.[0])}
            />
            <p className="form-help">Upload a square-ish logo (JPG, PNG, WebP). Optional URL below.</p>
            <label htmlFor="club-logo-url" className="club-registration__url-label">
              Or logo URL
            </label>
            <input
              id="club-logo-url"
              type="url"
              name="logoUrl"
              value={formData.logoUrl}
              onChange={handleChange}
              disabled={Boolean(logoFile)}
              placeholder="https://example.com/logo.png"
            />
            {logoPreviewSrc && (
              <div className="club-registration__preview">
                <img src={logoPreviewSrc} alt="Club logo preview" />
                {logoFile && (
                  <button type="button" className="btn btn-secondary" onClick={() => clearFile('logo')}>
                    Remove upload
                  </button>
                )}
              </div>
            )}
          </div>

          <div className="form-group club-registration__media">
            <label htmlFor="club-landing-file">Landing / cover image</label>
            <input
              id="club-landing-file"
              ref={landingInputRef}
              type="file"
              accept="image/jpeg,image/png,image/gif,image/webp"
              onChange={(e) => handleFileSelect('landing', e.target.files?.[0])}
            />
            <p className="form-help">Wide photo for the club page. Optional URL below.</p>
            <label htmlFor="club-image-url" className="club-registration__url-label">
              Or image URL
            </label>
            <input
              id="club-image-url"
              type="url"
              name="imageUrl"
              value={formData.imageUrl}
              onChange={handleChange}
              disabled={Boolean(landingFile)}
              placeholder="https://example.com/cover.jpg"
            />
            {landingPreviewSrc && (
              <div className="club-registration__preview club-registration__preview--wide">
                <img src={landingPreviewSrc} alt="Club landing preview" />
                {landingFile && (
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => clearFile('landing')}
                  >
                    Remove upload
                  </button>
                )}
              </div>
            )}
          </div>
        </div>

        <button type="submit" className="btn btn-primary" disabled={loading}>
          {loading ? 'Registering…' : 'Register Club'}
        </button>
      </form>
    </div>
  );
};

export default ClubRegistration;
